/* CONTEXT:START
Component: JoeConfigurationHistory
Status: build
Dependencies: Material-UI, mock data service
Next: Task 1.5 - AdminDashboard integration
CONTEXT:END */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Collapse
} from '@mui/material';
import {
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  Restore as RestoreIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { 
  JoeConfiguration,
  joeConfigurationApiService
} from '../../services/joeConfigurationApi';

// Helper function to display user name with fallback
const displayUserName = (config: JoeConfiguration): string => {
  if (config.createdByName && config.createdByName.trim()) {
    return config.createdByName;
  }
  
  // Fallback logic for configurations without user names
  if (config.createdBy.includes('@')) {
    return config.createdBy; // Email address
  }
  
  // UUID fallback - show shortened version
  return `User ${config.createdBy.substring(0, 8)}...`;
};

// Props interface
export interface JoeConfigurationHistoryProps {
  open: boolean;
  onClose: () => void;
  onRevert?: (configId: string) => void;
  currentActiveId?: string;
}

// Preview dialog props
interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  configuration: JoeConfiguration | null;
}

// Confirmation dialog props
interface ConfirmRevertDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  configuration: JoeConfiguration | null;
  isLoading: boolean;
}

// Preview Dialog Component
const PreviewDialog: React.FC<PreviewDialogProps> = ({ open, onClose, configuration }) => {
  if (!configuration) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Configuration Preview - Version {configuration.version}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Configuration Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Configuration Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={<PersonIcon />}
              label={configuration.createdBy}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<ScheduleIcon />}
              label={new Date(configuration.createdAt).toLocaleString()}
              size="small"
              variant="outlined"
            />
            {configuration.isActive && (
              <Chip
                label="Active"
                size="small"
                color="success"
                variant="filled"
              />
            )}
          </Box>
          {configuration.description && (
            <Typography variant="body2" color="text.secondary">
              <strong>Description:</strong> {configuration.description}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* System Prompt */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            System Prompt
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={configuration.systemPrompt}
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: '0.875rem',
                lineHeight: 1.5
              }
            }}
          />
        </Box>

        {/* User Prompt Template */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            User Prompt Template
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={configuration.userPromptTemplate}
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: '0.875rem',
                lineHeight: 1.5
              }
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Confirm Revert Dialog Component
const ConfirmRevertDialog: React.FC<ConfirmRevertDialogProps> = ({ 
  open, 
  onClose, 
  onConfirm, 
  configuration,
  isLoading 
}) => {
  if (!configuration) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <RestoreIcon sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6">
            Confirm Configuration Revert
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This action will replace the current active configuration 
            and immediately affect all future Joe evaluations.
          </Typography>
        </Alert>

        <Typography variant="body1" gutterBottom>
          Are you sure you want to revert to this configuration?
        </Typography>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Target Configuration:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Version:</strong> {configuration.version}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Created:</strong> {new Date(configuration.createdAt).toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Created By:</strong> {displayUserName(configuration)}
          </Typography>
          {configuration.description && (
            <Typography variant="body2" color="text.secondary">
              <strong>Description:</strong> {configuration.description}
            </Typography>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onConfirm}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <RestoreIcon />}
        >
          {isLoading ? 'Reverting...' : 'Revert Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main History Component
const JoeConfigurationHistory: React.FC<JoeConfigurationHistoryProps> = ({
  open,
  onClose,
  onRevert,
  currentActiveId
}) => {
  // State management
  const [configurations, setConfigurations] = useState<JoeConfiguration[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [previewConfig, setPreviewConfig] = useState<JoeConfiguration | null>(null);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [revertConfig, setRevertConfig] = useState<JoeConfiguration | null>(null);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState<boolean>(false);
  const [reverting, setReverting] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Load configuration history when dialog opens
  useEffect(() => {
    if (open) {
      loadConfigurationHistory();
    }
  }, [open]);

  const loadConfigurationHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const history = await joeConfigurationApiService.getConfigurationHistory(20);
      setConfigurations(history);
    } catch (err) {
      setError('Failed to load configuration history. Please try again.');
      console.error('Error loading configuration history:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle preview configuration
  const handlePreview = (config: JoeConfiguration) => {
    setPreviewConfig(config);
    setPreviewOpen(true);
  };

  // Handle revert configuration
  const handleRevert = (config: JoeConfiguration) => {
    if (config.isActive) {
      setError('Cannot revert to the currently active configuration.');
      return;
    }
    setRevertConfig(config);
    setRevertConfirmOpen(true);
  };

  // Confirm revert
  const handleConfirmRevert = async () => {
    if (!revertConfig) return;

    try {
      setReverting(true);
      setError(null);
      
      await joeConfigurationApiService.revertConfiguration(revertConfig.id);
      
      if (onRevert) {
        onRevert(revertConfig.id);
      }
      
      // Reload history to reflect changes
      await loadConfigurationHistory();
      
      setRevertConfirmOpen(false);
      setRevertConfig(null);
    } catch (err) {
      setError('Failed to revert configuration. Please try again.');
      console.error('Error reverting configuration:', err);
    } finally {
      setReverting(false);
    }
  };

  // Handle row expansion
  const handleToggleExpand = (configId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(configId)) {
      newExpanded.delete(configId);
    } else {
      newExpanded.add(configId);
    }
    setExpandedRows(newExpanded);
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Truncate text for table display
  const truncateText = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '600px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="div">
              Joe Configuration History
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            View and manage previous Joe configuration versions.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 0 }}>
          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mx: 3, mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading configuration history...
              </Typography>
            </Box>
          )}

          {/* History Table */}
          {!loading && configurations.length > 0 && (
            <TableContainer component={Paper} sx={{ mx: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="60px"></TableCell>
                    <TableCell><strong>Version</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Created</strong></TableCell>
                    <TableCell><strong>Created By</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell width="120px"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configurations.map((config) => (
                    <React.Fragment key={config.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleExpand(config.id)}
                          >
                            {expandedRows.has(config.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            v{config.version}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {config.isActive ? (
                            <Chip label="Active" size="small" color="success" />
                          ) : (
                            <Chip label="Inactive" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(config.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {displayUserName(config)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {config.description ? truncateText(config.description, 80) : 'No description'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Preview Configuration">
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(config)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={config.isActive ? "Cannot revert to active configuration" : "Revert to this configuration"}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevert(config)}
                                  disabled={config.isActive}
                                  color={config.isActive ? "default" : "warning"}
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Row Content */}
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0 }}>
                          <Collapse in={expandedRows.has(config.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                System Prompt Preview:
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {truncateText(config.systemPrompt, 200)}
                              </Typography>
                              
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                User Prompt Template Preview:
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {truncateText(config.userPromptTemplate, 200)}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Empty State */}
          {!loading && configurations.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <HistoryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Configuration History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configuration history will appear here as you make changes to Joe's prompts.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} startIcon={<CloseIcon />}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <PreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        configuration={previewConfig}
      />

      {/* Revert Confirmation Dialog */}
      <ConfirmRevertDialog
        open={revertConfirmOpen}
        onClose={() => setRevertConfirmOpen(false)}
        onConfirm={handleConfirmRevert}
        configuration={revertConfig}
        isLoading={reverting}
      />
    </>
  );
};

export default JoeConfigurationHistory;
